<script setup>
import { useForm, usePage } from "@inertiajs/vue3";
import { useToastStore } from "@/stores/toast.js";
import BrandButton from "@/Components/Brand/BrandButton.vue";

/**
 * "Account Settings" section of Manage Account (GH-922, Figma 271-1000):
 * email, currency, search visibility, connections privacy, organization
 * name, and the e-mail preference checkboxes, saving through Fortify's
 * profile-information endpoint. Name/username/photo and the welcome message are managed from
 * the Manage Profile & Theme modalog, so they're absent here — the endpoint
 * only validates the fields below.
 */
const props = defineProps({
    user: {
        type: Object,
        required: true,
    },
});

const currencies = usePage().props.currencies ?? {};

const form = useForm({
    _method: "PUT",
    email: props.user.data.email,
    preferred_currency: props.user.data.preferred_currency,
    exclude_from_search: props.user.data.exclude_from_search,
    hide_connections: props.user.data.hide_connections === true,
    organization_name: props.user.data.organization_name,
    email_new_follower: props.user.data.email_new_follower === 1,
    email_birthdays: props.user.data.email_birthdays === 1,
    email_events: props.user.data.email_events === 1,
    email_marketing: props.user.data.email_marketing === 1,
    email_gift_confirmations: props.user.data.email_gift_confirmations === 1,
});

const emailPreferences = [
    { key: "email_new_follower", label: "Email me when someone follows me" },
    {
        key: "email_birthdays",
        label: "Email me a reminder about upcoming birthdays",
    },
    { key: "email_events", label: "Email me a reminder about upcoming events" },
    { key: "email_marketing", label: "Subscribe to marketing emails" },
    {
        key: "email_gift_confirmations",
        label: "Email me when I confirm a gift",
    },
];

const toastStore = useToastStore();

function save() {
    form.post(route("user-profile-information.update"), {
        errorBag: "updateProfileInformation",
        preserveScroll: true,
        onSuccess: () =>
            toastStore.success("Your account settings were saved."),
        onError: () =>
            toastStore.error(
                "Your settings couldn’t be saved — please check the highlighted fields.",
            ),
    });
}

const labelClasses = "mb-1.5 block text-[15px] font-bold text-[#3c3938]";
const inputClasses =
    "h-11 w-full rounded-[5px] border border-solid border-[#a79e9b] bg-white px-4 font-brand text-[15px] text-brand-navy focus:border-brand-navy focus:outline-none focus:ring-0";
const selectClasses =
    "h-11 w-full appearance-none rounded-[5px] border border-solid border-[#a79e9b] bg-white bg-none py-0 pl-4 pr-9 font-brand text-[15px] text-brand-navy focus:border-brand-navy focus:outline-none focus:ring-0";
const caretClasses =
    "pointer-events-none absolute right-3.5 top-1/2 h-2.5 w-[13px] -translate-y-1/2 text-brand-red";
const errorClasses = "mt-2 text-[13px] font-semibold text-brand-red";
</script>

<template>
    <form @submit.prevent="save">
        <h2 class="font-brand text-[25px] font-bold text-[#102a3d]">
            Account Settings
        </h2>
        <p class="mt-1 font-brand text-[15px] leading-relaxed text-[#3c3938]">
            Update your email address or set your preferences. To change your
            profile, go to My Gift Hero and select Profile Options.
        </p>

        <div class="mt-8 gap-16 lg:grid lg:grid-cols-[433px_minmax(0,1fr)]">
            <div>
                <div>
                    <label :class="labelClasses" for="account-email"
                        >E-mail Address</label
                    >
                    <input
                        id="account-email"
                        v-model="form.email"
                        type="email"
                        autocomplete="email"
                        :class="inputClasses"
                    />
                    <p v-if="form.errors.email" :class="errorClasses">
                        {{ form.errors.email }}
                    </p>
                </div>

                <div class="mt-6">
                    <label :class="labelClasses" for="account-currency"
                        >Preferred Currency</label
                    >
                    <div class="relative">
                        <select
                            id="account-currency"
                            v-model="form.preferred_currency"
                            :class="selectClasses"
                        >
                            <option
                                v-for="(symbol, code) in currencies"
                                :key="code"
                                :value="code"
                            >
                                {{ code }} {{ symbol }}
                            </option>
                        </select>
                        <svg
                            viewBox="0 0 13 10"
                            :class="caretClasses"
                            fill="currentColor"
                            aria-hidden="true"
                        >
                            <path d="M6.5 10L0 0h13L6.5 10z" />
                        </svg>
                    </div>
                    <p
                        v-if="form.errors.preferred_currency"
                        :class="errorClasses"
                    >
                        {{ form.errors.preferred_currency }}
                    </p>
                </div>

                <div class="mt-6">
                    <label :class="labelClasses" for="account-search"
                        >Exclude from Search?</label
                    >
                    <div class="relative">
                        <select
                            id="account-search"
                            v-model="form.exclude_from_search"
                            :class="selectClasses"
                        >
                            <option :value="0">
                                Include me in &lsquo;Find Friends&rsquo;
                            </option>
                            <option :value="1">
                                Don&rsquo;t include me in &lsquo;Find
                                Friends&rsquo;
                            </option>
                        </select>
                        <svg
                            viewBox="0 0 13 10"
                            :class="caretClasses"
                            fill="currentColor"
                            aria-hidden="true"
                        >
                            <path d="M6.5 10L0 0h13L6.5 10z" />
                        </svg>
                    </div>
                    <p
                        class="mt-2 font-brand text-[13px] leading-snug text-[#3c3938]/80"
                    >
                        Users can
                        <a
                            :href="route('friends.find-friends')"
                            class="underline"
                            >search</a
                        >
                        for other users by first and last name, username, or
                        organization name.
                        <a
                            href="https://support.gifthero.com/page/sharing-and-connecting/#how-do-i-find-someone-or-an-organization-on-gift-hero"
                            target="_blank"
                            rel="noopener noreferrer"
                            class="underline"
                            >Learn More</a
                        >
                    </p>
                    <p
                        v-if="form.errors.exclude_from_search"
                        :class="errorClasses"
                    >
                        {{ form.errors.exclude_from_search }}
                    </p>
                </div>

                <div class="mt-6">
                    <label :class="labelClasses" for="account-connections"
                        >Followers &amp; Following</label
                    >
                    <div class="relative">
                        <select
                            id="account-connections"
                            v-model="form.hide_connections"
                            :class="selectClasses"
                        >
                            <option :value="false">
                                Show my followers and following to other users
                            </option>
                            <option :value="true">
                                Hide my followers and following from other users
                            </option>
                        </select>
                        <svg
                            viewBox="0 0 13 10"
                            :class="caretClasses"
                            fill="currentColor"
                            aria-hidden="true"
                        >
                            <path d="M6.5 10L0 0h13L6.5 10z" />
                        </svg>
                    </div>
                    <p
                        class="mt-2 font-brand text-[13px] leading-snug text-[#3c3938]/80"
                    >
                        When hidden, other users still see your follower and
                        following counts, but not who they are. You can always
                        see your own.
                    </p>
                    <p
                        v-if="form.errors.hide_connections"
                        :class="errorClasses"
                    >
                        {{ form.errors.hide_connections }}
                    </p>
                </div>

                <div class="mt-6">
                    <label :class="labelClasses" for="account-organization"
                        >Organization Name</label
                    >
                    <input
                        id="account-organization"
                        v-model="form.organization_name"
                        type="text"
                        autocomplete="organization"
                        :class="inputClasses"
                    />
                    <p
                        class="mt-2 font-brand text-[13px] leading-snug text-[#3c3938]/80"
                    >
                        Visible to all users.
                        <a
                            href="https://support.gifthero.com"
                            target="_blank"
                            rel="noopener noreferrer"
                            class="underline"
                            >Contact Support</a
                        >
                        to get your organization verified.
                    </p>
                    <p
                        v-if="form.errors.organization_name"
                        :class="errorClasses"
                    >
                        {{ form.errors.organization_name }}
                    </p>
                </div>
            </div>

            <div class="mt-8 flex flex-col lg:mt-0">
                <span :class="labelClasses">E-mail Preferences</span>
                <ul class="mt-1 flex flex-col gap-4">
                    <li
                        v-for="preference in emailPreferences"
                        :key="preference.key"
                    >
                        <label
                            class="flex items-center gap-3 font-brand text-[14px] text-[#3c3938]"
                        >
                            <input
                                v-model="form[preference.key]"
                                type="checkbox"
                                class="size-5 rounded-[2px] border-[#cdc4c2] text-brand-navy focus:ring-brand-navy"
                            />
                            {{ preference.label }}
                        </label>
                    </li>
                </ul>

                <div
                    class="mt-10 flex items-center justify-end gap-4 lg:mt-auto"
                >
                    <BrandButton
                        type="submit"
                        class="h-11 px-10 text-[15px]"
                        :loading="form.processing"
                    >
                        {{ form.processing ? "Saving" : "Save Settings" }}
                    </BrandButton>
                </div>
            </div>
        </div>
    </form>
</template>
